import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { fetchAllPaginated } from '../utils/productCatalog';
import { sanitizeProductDisplayName } from '../utils/productLabels';
import { getClientBalanceValue } from '../utils/clientBalances';
import { getProviderBalanceValue } from '../utils/providerBalances';
import {
  BarChart3,
  Boxes,
  Download,
  FileSpreadsheet,
  MapPin,
  Package,
  PieChart,
  RefreshCw,
  ShoppingBag,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import toast from 'react-hot-toast';

/* ──────────────────────────────────────────────────────────────
   Helpers
   ────────────────────────────────────────────────────────────── */
const fmtMoney = (v) =>
  Number(v || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (v) => Number(v || 0).toLocaleString('es-AR');
const fmtPct = (v) =>
  `${Number(v || 0).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const toLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const presetRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  return { from: toLocalDate(start), to: toLocalDate(end) };
};

const safeName = (raw, fallback = 'Sin nombre') => sanitizeProductDisplayName(raw || fallback, fallback);

const exportCsv = (filename, columns, rows) => {
  if (!rows?.length) {
    toast.error('No hay datos para exportar');
    return;
  }
  const escape = (val) => {
    const s = val == null ? '' : String(val);
    if (s.includes('"') || s.includes(',') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map((c) => escape(c.label)).join(',');
  const lines = rows.map((r) =>
    columns
      .map((c) => {
        const raw = typeof c.value === 'function' ? c.value(r) : r[c.key];
        return escape(c.exportFormat ? c.exportFormat(raw, r) : raw);
      })
      .join(',')
  );
  const csv = '﻿' + [header, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ──────────────────────────────────────────────────────────────
   UI atoms
   ────────────────────────────────────────────────────────────── */
function KpiCard({ icon: Icon, label, value, accent = 'bg-slate-100 text-slate-600', hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`grid h-10 w-10 place-items-center rounded-xl ${accent}`}>
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
          <p className="text-xl font-black text-slate-900 tracking-tighter truncate">{value}</p>
          {hint && <p className="text-[10px] font-medium text-slate-400">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ message = 'Sin datos para los filtros seleccionados' }) {
  return (
    <div className="py-16 flex flex-col items-center gap-3 text-slate-400">
      <div className="w-14 h-14 rounded-2xl bg-slate-50 grid place-items-center">
        <FileSpreadsheet size={22} />
      </div>
      <p className="text-[11px] font-black uppercase tracking-widest">{message}</p>
    </div>
  );
}

function ReportTable({ columns, rows, totals }) {
  if (!rows?.length) return <EmptyState />;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="min-w-full text-xs">
        <thead className="bg-slate-50">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-500 ${
                  c.align === 'right' ? 'text-right' : 'text-left'
                }`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {rows.map((r, idx) => (
            <tr key={r._key ?? idx} className="hover:bg-slate-50">
              {columns.map((c) => {
                const raw = typeof c.value === 'function' ? c.value(r) : r[c.key];
                const rendered = c.render ? c.render(raw, r) : raw;
                return (
                  <td
                    key={c.key}
                    className={`px-3 py-2 text-slate-700 ${c.align === 'right' ? 'text-right tabular-nums' : ''} ${
                      c.bold ? 'font-bold text-slate-900' : ''
                    }`}
                  >
                    {rendered ?? '-'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {totals && (
          <tfoot className="bg-slate-50 font-bold">
            <tr>
              {columns.map((c, i) => (
                <td
                  key={c.key}
                  className={`px-3 py-2.5 text-[11px] text-slate-800 ${
                    c.align === 'right' ? 'text-right tabular-nums' : ''
                  }`}
                >
                  {i === 0 && !totals[c.key] ? 'Totales' : c.renderTotal ? c.renderTotal(totals[c.key]) : totals[c.key] ?? ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Report fetchers
   firma: async (ctx) => { rows, kpis?, totals? }
   ctx: { orgId, fromIso, toIso, fromDate, toDate, siteId }
   ────────────────────────────────────────────────────────────── */

/* Estados a excluir de los reportes de ventas (anuladas, presupuestos, etc.)
   La DB tiene 3 variantes: 'canceled' (en, 1 L), 'cancelled' (en, 2 L), 'anulada' (es).
   'quote' = presupuesto, no es venta. */
const INVALID_SALE_STATUSES = ['canceled', 'cancelled', 'anulada', 'quote'];
const SALE_STATUS_NOT_IN = `(${INVALID_SALE_STATUSES.map((s) => `"${s}"`).join(',')})`;

const applySaleSite = (q, siteId) => (siteId ? q.eq('site_id', siteId) : q);
const applyItemSite = (q, siteId) => (siteId ? q.eq('venta.site_id', siteId) : q);
const applyValidSales = (q) => q.not('status', 'in', SALE_STATUS_NOT_IN);
const applyValidSalesViaJoin = (q) => q.not('venta.status', 'in', SALE_STATUS_NOT_IN);

/* Compras: estado en la DB es free-form con variantes de capitalización
   ('APROBADO', 'Aprobada', 'Pagada', 'ANULADO'). Filtramos client-side
   cualquier estado que empiece con "ANULAD" (case-insensitive). */
const isCompraValida = (c) => !String(c?.estado || '').toUpperCase().startsWith('ANULAD');

// ─── VENTAS ───────────────────────────────────────────────────
async function fetchProductosVendidos({ orgId, fromIso, toIso, siteId }) {
  const rows = await fetchAllPaginated(() =>
    applyItemSite(
      supabase
        .from('sale_items')
        .select(
          'quantity, unit_price, subtotal, current_unit_cost, current_total_cost, current_margin_amount, current_margin_pct, product_name_snapshot, producto:products!vinc_prod(id, name, brand, barcode), venta:sales!vinc_sale!inner(organization_id, created_at, status, site_id)'
        )
        .eq('venta.organization_id', orgId)
        .gte('venta.created_at', fromIso)
        .lte('venta.created_at', toIso)
        .not('venta.status', 'in', SALE_STATUS_NOT_IN),
      siteId
    )
  );

  const map = new Map();
  rows.forEach((it) => {
    const p = it.producto;
    const id = p?.id || `__${it.product_name_snapshot || 'sin-nombre'}`;
    const qty = Number(it.quantity || 0);
    const ingreso = Number(it.subtotal ?? qty * Number(it.unit_price || 0));
    const costo = Number(it.current_total_cost ?? qty * Number(it.current_unit_cost || 0));
    const entry = map.get(id) || {
      _key: id,
      producto: safeName(it.product_name_snapshot || p?.name),
      marca: p?.brand || '-',
      codigo: p?.barcode || '-',
      cantidad: 0,
      ingreso: 0,
      costo: 0,
    };
    entry.cantidad += qty;
    entry.ingreso += ingreso;
    entry.costo += costo;
    map.set(id, entry);
  });

  const data = Array.from(map.values())
    .map((e) => ({
      ...e,
      margen: e.ingreso - e.costo,
      margenPct: e.ingreso > 0 ? ((e.ingreso - e.costo) / e.ingreso) * 100 : 0,
    }))
    .sort((a, b) => b.ingreso - a.ingreso);

  const totalIngreso = data.reduce((s, r) => s + r.ingreso, 0);
  const totalCosto = data.reduce((s, r) => s + r.costo, 0);
  const totalCant = data.reduce((s, r) => s + r.cantidad, 0);

  return {
    rows: data,
    kpis: [
      { label: 'SKUs vendidos', value: fmtInt(data.length), icon: Package, accent: 'bg-blue-50 text-blue-600' },
      { label: 'Unidades', value: fmtInt(totalCant), icon: ShoppingBag, accent: 'bg-emerald-50 text-emerald-600' },
      { label: 'Ingreso bruto', value: `$ ${fmtMoney(totalIngreso)}`, icon: TrendingUp, accent: 'bg-violet-50 text-violet-600' },
      {
        label: 'Margen',
        value: `$ ${fmtMoney(totalIngreso - totalCosto)}`,
        icon: BarChart3,
        accent: 'bg-amber-50 text-amber-600',
        hint: totalIngreso > 0 ? fmtPct(((totalIngreso - totalCosto) / totalIngreso) * 100) : '0%',
      },
    ],
    totals: {
      cantidad: fmtInt(totalCant),
      ingreso: `$ ${fmtMoney(totalIngreso)}`,
      costo: `$ ${fmtMoney(totalCosto)}`,
      margen: `$ ${fmtMoney(totalIngreso - totalCosto)}`,
      margenPct: totalIngreso > 0 ? fmtPct(((totalIngreso - totalCosto) / totalIngreso) * 100) : '-',
    },
  };
}

async function fetchVentasPorCliente({ orgId, fromIso, toIso, siteId }) {
  const sales = await fetchAllPaginated(() =>
    applySaleSite(
      supabase
        .from('sales')
        .select('id, total, client_id, created_at, status, site_id, clients(full_name, doc_number)')
        .eq('organization_id', orgId)
        .gte('created_at', fromIso)
        .lte('created_at', toIso)
        .not('status', 'in', SALE_STATUS_NOT_IN),
      siteId
    )
  );

  const map = new Map();
  sales.forEach((s) => {
    const id = s.client_id || '__sin-cliente';
    const entry = map.get(id) || {
      _key: id,
      cliente: s.clients?.full_name || (s.client_id ? 'Cliente eliminado' : 'Consumidor final'),
      doc: s.clients?.doc_number || '-',
      tickets: 0,
      total: 0,
    };
    entry.tickets += 1;
    entry.total += Number(s.total || 0);
    map.set(id, entry);
  });

  const data = Array.from(map.values())
    .map((e) => ({ ...e, ticketProm: e.tickets > 0 ? e.total / e.tickets : 0 }))
    .sort((a, b) => b.total - a.total);

  const totalVentas = data.reduce((s, r) => s + r.total, 0);
  const totalTickets = data.reduce((s, r) => s + r.tickets, 0);

  return {
    rows: data,
    kpis: [
      { label: 'Clientes activos', value: fmtInt(data.length), icon: Users, accent: 'bg-blue-50 text-blue-600' },
      { label: 'Tickets', value: fmtInt(totalTickets), icon: ShoppingBag, accent: 'bg-emerald-50 text-emerald-600' },
      { label: 'Facturación', value: `$ ${fmtMoney(totalVentas)}`, icon: TrendingUp, accent: 'bg-violet-50 text-violet-600' },
      {
        label: 'Ticket promedio',
        value: `$ ${fmtMoney(totalTickets > 0 ? totalVentas / totalTickets : 0)}`,
        icon: BarChart3,
        accent: 'bg-amber-50 text-amber-600',
      },
    ],
    totals: {
      tickets: fmtInt(totalTickets),
      total: `$ ${fmtMoney(totalVentas)}`,
      ticketProm: `$ ${fmtMoney(totalTickets > 0 ? totalVentas / totalTickets : 0)}`,
    },
  };
}

async function fetchVentasPorMetodoCobro({ orgId, fromIso, toIso, siteId }) {
  const sales = await fetchAllPaginated(() =>
    applySaleSite(
      supabase
        .from('sales')
        .select('id, total, payment_method, payment_method_id, status, site_id, created_at, payment_methods(name)')
        .eq('organization_id', orgId)
        .gte('created_at', fromIso)
        .lte('created_at', toIso)
        .not('status', 'in', SALE_STATUS_NOT_IN),
      siteId
    )
  );

  const map = new Map();
  sales.forEach((s) => {
    const key = s.payment_methods?.name || s.payment_method || 'Sin método';
    const entry = map.get(key) || { _key: key, metodo: key, tickets: 0, total: 0 };
    entry.tickets += 1;
    entry.total += Number(s.total || 0);
    map.set(key, entry);
  });

  const data = Array.from(map.values()).sort((a, b) => b.total - a.total);
  const totalVentas = data.reduce((s, r) => s + r.total, 0);
  data.forEach((r) => {
    r.pct = totalVentas > 0 ? (r.total / totalVentas) * 100 : 0;
  });

  return {
    rows: data,
    kpis: [
      { label: 'Métodos usados', value: fmtInt(data.length), icon: Wallet, accent: 'bg-blue-50 text-blue-600' },
      { label: 'Tickets', value: fmtInt(data.reduce((s, r) => s + r.tickets, 0)), icon: ShoppingBag, accent: 'bg-emerald-50 text-emerald-600' },
      { label: 'Total cobrado', value: `$ ${fmtMoney(totalVentas)}`, icon: TrendingUp, accent: 'bg-violet-50 text-violet-600' },
    ],
    totals: { tickets: fmtInt(data.reduce((s, r) => s + r.tickets, 0)), total: `$ ${fmtMoney(totalVentas)}`, pct: '100%' },
  };
}

async function fetchVentasPorSitio({ orgId, fromIso, toIso, sites }) {
  const sales = await fetchAllPaginated(() =>
    supabase
      .from('sales')
      .select('id, total, site_id, status, created_at')
      .eq('organization_id', orgId)
      .gte('created_at', fromIso)
      .lte('created_at', toIso)
      .not('status', 'in', SALE_STATUS_NOT_IN)
  );

  const siteMap = new Map((sites || []).map((s) => [String(s.id), s.nombre]));
  const map = new Map();
  sales.forEach((s) => {
    const id = s.site_id ? String(s.site_id) : '__sin-sitio';
    const entry = map.get(id) || {
      _key: id,
      sitio: siteMap.get(id) || (s.site_id ? 'Sitio eliminado' : 'Sin sitio'),
      tickets: 0,
      total: 0,
    };
    entry.tickets += 1;
    entry.total += Number(s.total || 0);
    map.set(id, entry);
  });

  const data = Array.from(map.values())
    .map((e) => ({ ...e, ticketProm: e.tickets > 0 ? e.total / e.tickets : 0 }))
    .sort((a, b) => b.total - a.total);

  const totalVentas = data.reduce((s, r) => s + r.total, 0);
  data.forEach((r) => {
    r.pct = totalVentas > 0 ? (r.total / totalVentas) * 100 : 0;
  });

  return {
    rows: data,
    kpis: [
      { label: 'Sitios con venta', value: fmtInt(data.length), icon: MapPin, accent: 'bg-blue-50 text-blue-600' },
      { label: 'Tickets', value: fmtInt(data.reduce((s, r) => s + r.tickets, 0)), icon: ShoppingBag, accent: 'bg-emerald-50 text-emerald-600' },
      { label: 'Total', value: `$ ${fmtMoney(totalVentas)}`, icon: TrendingUp, accent: 'bg-violet-50 text-violet-600' },
    ],
    totals: {
      tickets: fmtInt(data.reduce((s, r) => s + r.tickets, 0)),
      total: `$ ${fmtMoney(totalVentas)}`,
      ticketProm: `$ ${fmtMoney(totalVentas > 0 ? totalVentas / data.reduce((s, r) => s + r.tickets, 0) : 0)}`,
      pct: '100%',
    },
  };
}

async function fetchVentasPorVendedor({ orgId, fromIso, toIso, siteId }) {
  const sales = await fetchAllPaginated(() =>
    applySaleSite(
      supabase
        .from('sales')
        .select('id, total, user_id, status, site_id, created_at')
        .eq('organization_id', orgId)
        .gte('created_at', fromIso)
        .lte('created_at', toIso)
        .not('status', 'in', SALE_STATUS_NOT_IN),
      siteId
    )
  );

  const userIds = Array.from(new Set(sales.map((s) => s.user_id).filter(Boolean)));
  let profileMap = new Map();
  if (userIds.length > 0) {
    const { data: profs } = await supabase
      .from('profiles')
      .select('id, user_id, full_name, email')
      .eq('organization_id', orgId)
      .in('user_id', userIds);
    profileMap = new Map((profs || []).map((p) => [p.user_id, p]));
  }

  const map = new Map();
  sales.forEach((s) => {
    const id = s.user_id || '__sin-vendedor';
    const prof = profileMap.get(s.user_id);
    const entry = map.get(id) || {
      _key: id,
      vendedor: prof?.full_name || prof?.email || (s.user_id ? 'Usuario eliminado' : 'Sin vendedor'),
      email: prof?.email || '-',
      tickets: 0,
      total: 0,
    };
    entry.tickets += 1;
    entry.total += Number(s.total || 0);
    map.set(id, entry);
  });

  const data = Array.from(map.values())
    .map((e) => ({ ...e, ticketProm: e.tickets > 0 ? e.total / e.tickets : 0 }))
    .sort((a, b) => b.total - a.total);

  const totalVentas = data.reduce((s, r) => s + r.total, 0);
  return {
    rows: data,
    kpis: [
      { label: 'Vendedores', value: fmtInt(data.length), icon: Users, accent: 'bg-blue-50 text-blue-600' },
      { label: 'Tickets', value: fmtInt(data.reduce((s, r) => s + r.tickets, 0)), icon: ShoppingBag, accent: 'bg-emerald-50 text-emerald-600' },
      { label: 'Total facturado', value: `$ ${fmtMoney(totalVentas)}`, icon: TrendingUp, accent: 'bg-violet-50 text-violet-600' },
    ],
    totals: { tickets: fmtInt(data.reduce((s, r) => s + r.tickets, 0)), total: `$ ${fmtMoney(totalVentas)}`, ticketProm: '' },
  };
}

async function fetchProductosNoVendidos({ orgId, fromIso, toIso, siteId }) {
  const [itemsRows, productsRows] = await Promise.all([
    fetchAllPaginated(() =>
      applyItemSite(
        supabase
          .from('sale_items')
          .select('product_id, venta:sales!vinc_sale!inner(organization_id, created_at, status, site_id)')
          .eq('venta.organization_id', orgId)
          .gte('venta.created_at', fromIso)
          .lte('venta.created_at', toIso)
          .not('venta.status', 'in', SALE_STATUS_NOT_IN),
        siteId
      )
    ),
    fetchAllPaginated(() =>
      supabase
        .from('products')
        .select('id, name, brand, barcode, cost_price, sell_price, stock_levels(quantity, site_id)')
        .eq('organization_id', orgId)
        .eq('active', true)
        .order('name', { ascending: true })
    ),
  ]);

  const vendidos = new Set(itemsRows.map((it) => it.product_id).filter(Boolean));
  const data = productsRows
    .filter((p) => !vendidos.has(p.id))
    .map((p) => {
      const stock = (p.stock_levels || [])
        .filter((sl) => (siteId ? String(sl.site_id) === String(siteId) : true))
        .reduce((s, sl) => s + Number(sl.quantity || 0), 0);
      return {
        _key: p.id,
        producto: safeName(p.name),
        marca: p.brand || '-',
        codigo: p.barcode || '-',
        stock,
        costo: Number(p.cost_price || 0),
        precio: Number(p.sell_price || 0),
        valorStock: stock * Number(p.cost_price || 0),
      };
    })
    .sort((a, b) => b.valorStock - a.valorStock);

  return {
    rows: data,
    kpis: [
      { label: 'Productos sin venta', value: fmtInt(data.length), icon: Package, accent: 'bg-rose-50 text-rose-600' },
      {
        label: 'Stock inmovilizado',
        value: fmtInt(data.reduce((s, r) => s + r.stock, 0)),
        icon: Boxes,
        accent: 'bg-amber-50 text-amber-600',
      },
      {
        label: 'Valor inmovilizado',
        value: `$ ${fmtMoney(data.reduce((s, r) => s + r.valorStock, 0))}`,
        icon: Wallet,
        accent: 'bg-violet-50 text-violet-600',
      },
    ],
  };
}

// ─── INVENTARIO ───────────────────────────────────────────────
async function fetchStockValorizado({ orgId, siteId }) {
  const products = await fetchAllPaginated(() =>
    supabase
      .from('products')
      .select('id, name, brand, barcode, cost_price, sell_price, stock_levels(quantity, site_id)')
      .eq('organization_id', orgId)
      .eq('active', true)
      .order('name', { ascending: true })
  );

  const data = products
    .map((p) => {
      const stock = (p.stock_levels || [])
        .filter((sl) => (siteId ? String(sl.site_id) === String(siteId) : true))
        .reduce((s, sl) => s + Number(sl.quantity || 0), 0);
      const costo = Number(p.cost_price || 0);
      const precio = Number(p.sell_price || 0);
      return {
        _key: p.id,
        producto: safeName(p.name),
        marca: p.brand || '-',
        codigo: p.barcode || '-',
        stock,
        costoUnit: costo,
        valorCosto: stock * costo,
        precio,
        valorVenta: stock * precio,
        margenPotencial: stock * (precio - costo),
      };
    })
    .filter((r) => r.stock > 0)
    .sort((a, b) => b.valorCosto - a.valorCosto);

  const totalCosto = data.reduce((s, r) => s + r.valorCosto, 0);
  const totalVenta = data.reduce((s, r) => s + r.valorVenta, 0);

  return {
    rows: data,
    kpis: [
      { label: 'SKUs con stock', value: fmtInt(data.length), icon: Package, accent: 'bg-blue-50 text-blue-600' },
      {
        label: 'Unidades totales',
        value: fmtInt(data.reduce((s, r) => s + r.stock, 0)),
        icon: Boxes,
        accent: 'bg-emerald-50 text-emerald-600',
      },
      { label: 'Valor a costo', value: `$ ${fmtMoney(totalCosto)}`, icon: Wallet, accent: 'bg-amber-50 text-amber-600' },
      { label: 'Valor a venta', value: `$ ${fmtMoney(totalVenta)}`, icon: TrendingUp, accent: 'bg-violet-50 text-violet-600' },
    ],
    totals: {
      stock: fmtInt(data.reduce((s, r) => s + r.stock, 0)),
      valorCosto: `$ ${fmtMoney(totalCosto)}`,
      valorVenta: `$ ${fmtMoney(totalVenta)}`,
      margenPotencial: `$ ${fmtMoney(totalVenta - totalCosto)}`,
    },
  };
}

async function fetchBajoMinimo({ orgId, siteId }) {
  const products = await fetchAllPaginated(() =>
    supabase
      .from('products')
      .select('id, name, brand, barcode, cost_price, min_stock, stock_levels(quantity, site_id, min_stock)')
      .eq('organization_id', orgId)
      .eq('active', true)
      .order('name', { ascending: true })
  );

  const data = [];
  products.forEach((p) => {
    const levels = (p.stock_levels || []).filter((sl) =>
      siteId ? String(sl.site_id) === String(siteId) : true
    );

    if (siteId) {
      const sl = levels[0];
      const qty = Number(sl?.quantity || 0);
      const min = Number(sl?.min_stock ?? p.min_stock ?? 0);
      if (min > 0 && qty < min) {
        data.push({
          _key: `${p.id}-${siteId}`,
          producto: safeName(p.name),
          marca: p.brand || '-',
          codigo: p.barcode || '-',
          stock: qty,
          minimo: min,
          faltante: min - qty,
          costoUnit: Number(p.cost_price || 0),
          reposicion: (min - qty) * Number(p.cost_price || 0),
        });
      }
    } else {
      const stock = levels.reduce((s, sl) => s + Number(sl.quantity || 0), 0);
      const min = Number(p.min_stock || 0);
      if (min > 0 && stock < min) {
        data.push({
          _key: p.id,
          producto: safeName(p.name),
          marca: p.brand || '-',
          codigo: p.barcode || '-',
          stock,
          minimo: min,
          faltante: min - stock,
          costoUnit: Number(p.cost_price || 0),
          reposicion: (min - stock) * Number(p.cost_price || 0),
        });
      }
    }
  });

  data.sort((a, b) => b.faltante - a.faltante);
  const totalRep = data.reduce((s, r) => s + r.reposicion, 0);

  return {
    rows: data,
    kpis: [
      { label: 'SKUs bajo mínimo', value: fmtInt(data.length), icon: Package, accent: 'bg-rose-50 text-rose-600' },
      {
        label: 'Unidades a reponer',
        value: fmtInt(data.reduce((s, r) => s + r.faltante, 0)),
        icon: Boxes,
        accent: 'bg-amber-50 text-amber-600',
      },
      { label: 'Costo reposición', value: `$ ${fmtMoney(totalRep)}`, icon: Wallet, accent: 'bg-violet-50 text-violet-600' },
    ],
    totals: {
      stock: fmtInt(data.reduce((s, r) => s + r.stock, 0)),
      minimo: fmtInt(data.reduce((s, r) => s + r.minimo, 0)),
      faltante: fmtInt(data.reduce((s, r) => s + r.faltante, 0)),
      reposicion: `$ ${fmtMoney(totalRep)}`,
    },
  };
}

async function fetchRotacion({ orgId, fromIso, toIso, fromDate, toDate, siteId }) {
  const [items, products] = await Promise.all([
    fetchAllPaginated(() =>
      applyItemSite(
        supabase
          .from('sale_items')
          .select(
            'product_id, quantity, venta:sales!vinc_sale!inner(organization_id, created_at, status, site_id)'
          )
          .eq('venta.organization_id', orgId)
          .gte('venta.created_at', fromIso)
          .lte('venta.created_at', toIso)
          .not('venta.status', 'in', SALE_STATUS_NOT_IN),
        siteId
      )
    ),
    fetchAllPaginated(() =>
      supabase
        .from('products')
        .select('id, name, brand, cost_price, stock_levels(quantity, site_id)')
        .eq('organization_id', orgId)
        .eq('active', true)
    ),
  ]);

  const sold = new Map();
  items.forEach((it) => {
    if (!it.product_id) return;
    sold.set(it.product_id, (sold.get(it.product_id) || 0) + Number(it.quantity || 0));
  });

  const days = Math.max(1, Math.round((new Date(toDate) - new Date(fromDate)) / 86400000) + 1);

  const data = products
    .map((p) => {
      const stock = (p.stock_levels || [])
        .filter((sl) => (siteId ? String(sl.site_id) === String(siteId) : true))
        .reduce((s, sl) => s + Number(sl.quantity || 0), 0);
      const vendido = sold.get(p.id) || 0;
      const promDia = vendido / days;
      const cobertura = promDia > 0 ? stock / promDia : null;
      return {
        _key: p.id,
        producto: safeName(p.name),
        marca: p.brand || '-',
        stock,
        vendido,
        promDia,
        cobertura,
      };
    })
    .filter((r) => r.vendido > 0 || r.stock > 0)
    .sort((a, b) => (a.cobertura ?? Infinity) - (b.cobertura ?? Infinity));

  return {
    rows: data,
    kpis: [
      { label: 'Productos analizados', value: fmtInt(data.length), icon: Package, accent: 'bg-blue-50 text-blue-600' },
      { label: 'Días del período', value: fmtInt(days), icon: TrendingUp, accent: 'bg-emerald-50 text-emerald-600' },
      {
        label: 'Cobertura prom. (días)',
        value: (() => {
          const valid = data.filter((r) => r.cobertura != null && Number.isFinite(r.cobertura));
          if (!valid.length) return '-';
          return Math.round(valid.reduce((s, r) => s + r.cobertura, 0) / valid.length).toLocaleString('es-AR');
        })(),
        icon: BarChart3,
        accent: 'bg-amber-50 text-amber-600',
      },
    ],
  };
}

async function fetchKardex({ orgId, fromIso, toIso, siteId }) {
  const movs = await fetchAllPaginated(() => {
    let q = supabase
      .from('movimientos_stock')
      .select(
        'id, created_at, tipo, cantidad, motivo, producto_id, sitio_origen, sitio_destino, products(name, brand, barcode)'
      )
      .eq('organization_id', orgId)
      .gte('created_at', fromIso)
      .lte('created_at', toIso)
      .order('created_at', { ascending: false });
    if (siteId) q = q.or(`sitio_origen.eq.${siteId},sitio_destino.eq.${siteId}`);
    return q;
  });

  const data = movs.map((m) => {
    const t = String(m.tipo || '').toLowerCase();
    const esIngreso = t.includes('ingreso');
    const esEgreso = t.includes('egreso') || t.includes('venta') || t.includes('salida');
    return {
      _key: m.id,
      fecha: m.created_at,
      producto: safeName(m.products?.name),
      marca: m.products?.brand || '-',
      tipo: m.tipo || '-',
      cantidad: Number(m.cantidad || 0),
      ingreso: esIngreso ? Number(m.cantidad || 0) : 0,
      egreso: esEgreso ? Number(m.cantidad || 0) : 0,
      motivo: m.motivo || '-',
    };
  });

  return {
    rows: data,
    kpis: [
      { label: 'Movimientos', value: fmtInt(data.length), icon: BarChart3, accent: 'bg-blue-50 text-blue-600' },
      {
        label: 'Ingresos (unid.)',
        value: fmtInt(data.reduce((s, r) => s + r.ingreso, 0)),
        icon: TrendingUp,
        accent: 'bg-emerald-50 text-emerald-600',
      },
      {
        label: 'Egresos (unid.)',
        value: fmtInt(data.reduce((s, r) => s + r.egreso, 0)),
        icon: Package,
        accent: 'bg-rose-50 text-rose-600',
      },
    ],
  };
}

// ─── COMPRAS ──────────────────────────────────────────────────
async function fetchComprasPorProveedor({ orgId, fromIso, toIso }) {
  const comprasRaw = await fetchAllPaginated(() =>
    supabase
      .from('compras')
      .select('id, proveedor_id, proveedor_nombre, total_general, fecha, condicion_pago, estado')
      .eq('organization_id', orgId)
      .gte('fecha', fromIso)
      .lte('fecha', toIso)
      .order('fecha', { ascending: false })
  );
  const compras = comprasRaw.filter(isCompraValida);

  const map = new Map();
  compras.forEach((c) => {
    const id = c.proveedor_id || `__${c.proveedor_nombre || 'sin-prov'}`;
    const entry = map.get(id) || {
      _key: id,
      proveedor: c.proveedor_nombre || 'Sin proveedor',
      ops: 0,
      total: 0,
    };
    entry.ops += 1;
    entry.total += Number(c.total_general || 0);
    map.set(id, entry);
  });

  const data = Array.from(map.values())
    .map((e) => ({ ...e, opProm: e.ops > 0 ? e.total / e.ops : 0 }))
    .sort((a, b) => b.total - a.total);

  return {
    rows: data,
    kpis: [
      { label: 'Proveedores', value: fmtInt(data.length), icon: Users, accent: 'bg-blue-50 text-blue-600' },
      { label: 'Operaciones', value: fmtInt(data.reduce((s, r) => s + r.ops, 0)), icon: ShoppingBag, accent: 'bg-emerald-50 text-emerald-600' },
      { label: 'Total comprado', value: `$ ${fmtMoney(data.reduce((s, r) => s + r.total, 0))}`, icon: TrendingUp, accent: 'bg-violet-50 text-violet-600' },
    ],
    totals: {
      ops: fmtInt(data.reduce((s, r) => s + r.ops, 0)),
      total: `$ ${fmtMoney(data.reduce((s, r) => s + r.total, 0))}`,
      opProm: '',
    },
  };
}

async function fetchProductosMasComprados({ orgId, fromIso, toIso }) {
  const comprasRaw = await fetchAllPaginated(() =>
    supabase
      .from('compras')
      .select('id, items, fecha, estado')
      .eq('organization_id', orgId)
      .gte('fecha', fromIso)
      .lte('fecha', toIso)
  );
  const compras = comprasRaw.filter(isCompraValida);

  const map = new Map();
  compras.forEach((c) => {
    const items = Array.isArray(c.items) ? c.items : [];
    items.forEach((it) => {
      const name = it.nombre || it.name || it.producto || it.descripcion || it.product_name || 'Sin nombre';
      const id = it.product_id || it.producto_id || `__${name}`;
      const qty = Number(it.cantidad ?? it.quantity ?? 0);
      const costoUnit = Number(it.costo ?? it.cost ?? it.unit_cost ?? it.precio_unitario ?? 0);
      const subtotal = Number(it.subtotal ?? qty * costoUnit);
      const entry = map.get(id) || { _key: id, producto: safeName(name), cantidad: 0, costo: 0 };
      entry.cantidad += qty;
      entry.costo += subtotal;
      map.set(id, entry);
    });
  });

  const data = Array.from(map.values())
    .map((e) => ({ ...e, costoProm: e.cantidad > 0 ? e.costo / e.cantidad : 0 }))
    .sort((a, b) => b.costo - a.costo);

  return {
    rows: data,
    kpis: [
      { label: 'SKUs comprados', value: fmtInt(data.length), icon: Package, accent: 'bg-blue-50 text-blue-600' },
      { label: 'Unidades', value: fmtInt(data.reduce((s, r) => s + r.cantidad, 0)), icon: Boxes, accent: 'bg-emerald-50 text-emerald-600' },
      { label: 'Total invertido', value: `$ ${fmtMoney(data.reduce((s, r) => s + r.costo, 0))}`, icon: Wallet, accent: 'bg-violet-50 text-violet-600' },
    ],
    totals: {
      cantidad: fmtInt(data.reduce((s, r) => s + r.cantidad, 0)),
      costo: `$ ${fmtMoney(data.reduce((s, r) => s + r.costo, 0))}`,
      costoProm: '',
    },
  };
}

async function fetchEvolucionCostos({ orgId, fromIso, toIso }) {
  const hist = await fetchAllPaginated(() =>
    supabase
      .from('product_cost_history')
      .select('id, product_id, cost_price_previo, cost_price_nuevo, source, changed_at, products(name, brand)')
      .eq('organization_id', orgId)
      .gte('changed_at', fromIso)
      .lte('changed_at', toIso)
      .order('changed_at', { ascending: false })
  );

  const data = (hist || []).map((h) => {
    const prev = Number(h.cost_price_previo || 0);
    const nuevo = Number(h.cost_price_nuevo || 0);
    const variacion = nuevo - prev;
    const variacionPct = prev > 0 ? (variacion / prev) * 100 : 0;
    return {
      _key: h.id,
      fecha: h.changed_at,
      producto: safeName(h.products?.name),
      marca: h.products?.brand || '-',
      origen: h.source || '-',
      costoPrev: prev,
      costoNuevo: nuevo,
      variacion,
      variacionPct,
    };
  });

  return {
    rows: data,
    kpis: [
      { label: 'Cambios de costo', value: fmtInt(data.length), icon: BarChart3, accent: 'bg-blue-50 text-blue-600' },
      {
        label: 'SKUs afectados',
        value: fmtInt(new Set(data.map((r) => r._key && r.producto)).size),
        icon: Package,
        accent: 'bg-emerald-50 text-emerald-600',
      },
      {
        label: 'Variación promedio',
        value: data.length
          ? fmtPct(data.reduce((s, r) => s + r.variacionPct, 0) / data.length)
          : '-',
        icon: TrendingUp,
        accent: 'bg-amber-50 text-amber-600',
      },
    ],
  };
}

// ─── TESORERÍA ────────────────────────────────────────────────
async function fetchGastosPorCategoria({ orgId, fromDate, toDate, siteId }) {
  const gastos = await fetchAllPaginated(() => {
    let q = supabase
      .from('gastos')
      .select('id, categoria, monto, fecha, concepto, sitio_id')
      .eq('organization_id', orgId)
      .eq('is_active', true)
      .gte('fecha', fromDate)
      .lte('fecha', toDate)
      .order('fecha', { ascending: false });
    if (siteId) q = q.eq('sitio_id', siteId);
    return q;
  });

  const map = new Map();
  (gastos || []).forEach((g) => {
    const cat = g.categoria || 'Sin categoría';
    const entry = map.get(cat) || { _key: cat, categoria: cat, cantidad: 0, total: 0 };
    entry.cantidad += 1;
    entry.total += Number(g.monto || 0);
    map.set(cat, entry);
  });

  const data = Array.from(map.values()).sort((a, b) => b.total - a.total);
  const totalGral = data.reduce((s, r) => s + r.total, 0);
  data.forEach((r) => {
    r.pct = totalGral > 0 ? (r.total / totalGral) * 100 : 0;
  });

  return {
    rows: data,
    kpis: [
      { label: 'Categorías', value: fmtInt(data.length), icon: PieChart, accent: 'bg-blue-50 text-blue-600' },
      { label: 'Movimientos', value: fmtInt(data.reduce((s, r) => s + r.cantidad, 0)), icon: BarChart3, accent: 'bg-emerald-50 text-emerald-600' },
      { label: 'Total gastado', value: `$ ${fmtMoney(totalGral)}`, icon: Wallet, accent: 'bg-rose-50 text-rose-600' },
    ],
    totals: {
      cantidad: fmtInt(data.reduce((s, r) => s + r.cantidad, 0)),
      total: `$ ${fmtMoney(totalGral)}`,
      pct: '100%',
    },
  };
}

const agingBuckets = (days) => {
  if (days <= 30) return '0-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  return '+90';
};

async function fetchAgingCobrar({ orgId }) {
  const clients = await fetchAllPaginated(() =>
    supabase
      .from('clients')
      .select('id, full_name, doc_number, balance, current_balance')
      .eq('organization_id', orgId)
      .eq('is_active', true)
  );

  const conSaldo = clients
    .map((c) => ({ ...c, _saldo: getClientBalanceValue(c) }))
    .filter((c) => c._saldo > 0);

  const movsByClient = new Map();
  if (conSaldo.length > 0) {
    const ids = conSaldo.map((c) => c.id);
    const movs = await fetchAllPaginated(() =>
      supabase
        .from('client_movements')
        .select('client_id, created_at, amount, type')
        .eq('organization_id', orgId)
        .in('client_id', ids)
        .order('created_at', { ascending: false })
    );
    movs.forEach((m) => {
      if (!movsByClient.has(m.client_id)) movsByClient.set(m.client_id, m.created_at);
    });
  }

  const hoy = new Date();
  const data = conSaldo
    .map((c) => {
      const ultMov = movsByClient.get(c.id);
      const dias = ultMov ? Math.floor((hoy - new Date(ultMov)) / 86400000) : 0;
      return {
        _key: c.id,
        cliente: c.full_name || 'Sin nombre',
        doc: c.doc_number || '-',
        ultMov,
        dias,
        bucket: agingBuckets(dias),
        saldo: c._saldo,
      };
    })
    .sort((a, b) => b.saldo - a.saldo);

  const buckets = { '0-30': 0, '31-60': 0, '61-90': 0, '+90': 0 };
  data.forEach((r) => {
    buckets[r.bucket] += r.saldo;
  });

  return {
    rows: data,
    kpis: [
      { label: '0-30 días', value: `$ ${fmtMoney(buckets['0-30'])}`, icon: Wallet, accent: 'bg-emerald-50 text-emerald-600' },
      { label: '31-60 días', value: `$ ${fmtMoney(buckets['31-60'])}`, icon: Wallet, accent: 'bg-amber-50 text-amber-600' },
      { label: '61-90 días', value: `$ ${fmtMoney(buckets['61-90'])}`, icon: Wallet, accent: 'bg-orange-50 text-orange-600' },
      { label: '+90 días', value: `$ ${fmtMoney(buckets['+90'])}`, icon: Wallet, accent: 'bg-rose-50 text-rose-600' },
    ],
    totals: {
      dias: '',
      saldo: `$ ${fmtMoney(data.reduce((s, r) => s + r.saldo, 0))}`,
    },
  };
}

async function fetchAgingPagar({ orgId }) {
  const provs = await fetchAllPaginated(() =>
    supabase
      .from('proveedores')
      .select('id, razon_social, cuit, saldo')
      .eq('organization_id', orgId)
      .eq('is_active', true)
  );

  const conSaldo = provs
    .map((p) => ({ ...p, _saldo: getProviderBalanceValue(p) }))
    .filter((p) => p._saldo > 0);

  const movsByProv = new Map();
  if (conSaldo.length > 0) {
    const ids = conSaldo.map((p) => p.id);
    const movs = await fetchAllPaginated(() =>
      supabase
        .from('proveedor_movimientos')
        .select('proveedor_id, fecha, monto, tipo')
        .eq('organization_id', orgId)
        .in('proveedor_id', ids)
        .order('fecha', { ascending: false })
    );
    movs.forEach((m) => {
      if (!movsByProv.has(m.proveedor_id)) movsByProv.set(m.proveedor_id, m.fecha);
    });
  }

  const hoy = new Date();
  const data = conSaldo
    .map((p) => {
      const ult = movsByProv.get(p.id);
      const dias = ult ? Math.floor((hoy - new Date(ult)) / 86400000) : 0;
      return {
        _key: p.id,
        proveedor: p.razon_social || 'Sin nombre',
        cuit: p.cuit || '-',
        ultMov: ult,
        dias,
        bucket: agingBuckets(dias),
        saldo: p._saldo,
      };
    })
    .sort((a, b) => b.saldo - a.saldo);

  const buckets = { '0-30': 0, '31-60': 0, '61-90': 0, '+90': 0 };
  data.forEach((r) => {
    buckets[r.bucket] += r.saldo;
  });

  return {
    rows: data,
    kpis: [
      { label: '0-30 días', value: `$ ${fmtMoney(buckets['0-30'])}`, icon: Wallet, accent: 'bg-emerald-50 text-emerald-600' },
      { label: '31-60 días', value: `$ ${fmtMoney(buckets['31-60'])}`, icon: Wallet, accent: 'bg-amber-50 text-amber-600' },
      { label: '61-90 días', value: `$ ${fmtMoney(buckets['61-90'])}`, icon: Wallet, accent: 'bg-orange-50 text-orange-600' },
      { label: '+90 días', value: `$ ${fmtMoney(buckets['+90'])}`, icon: Wallet, accent: 'bg-rose-50 text-rose-600' },
    ],
    totals: {
      dias: '',
      saldo: `$ ${fmtMoney(data.reduce((s, r) => s + r.saldo, 0))}`,
    },
  };
}

async function fetchCobrosYPagos({ orgId, fromIso, toIso, siteId }) {
  const movs = await fetchAllPaginated(() => {
    let q = supabase
      .from('movimientos_financieros')
      .select('id, fecha_operacion, tipo, categoria, monto, descripcion, entidad_tipo, sitio_id')
      .eq('organization_id', orgId)
      .eq('is_active', true)
      .gte('fecha_operacion', fromIso)
      .lte('fecha_operacion', toIso)
      .order('fecha_operacion', { ascending: false });
    if (siteId) q = q.eq('sitio_id', siteId);
    return q;
  });

  const data = (movs || []).map((m) => {
    const t = String(m.tipo || '').toLowerCase();
    const monto = Number(m.monto || 0);
    const esEntrada = t.includes('ingreso') || t.includes('cobro') || t.includes('credito');
    const esSalida = t.includes('egreso') || t.includes('pago') || t.includes('debito');
    return {
      _key: m.id,
      fecha: m.fecha_operacion,
      tipo: m.tipo || '-',
      categoria: m.categoria || '-',
      descripcion: m.descripcion || '-',
      entrada: esEntrada ? monto : 0,
      salida: esSalida ? monto : 0,
      neto: esEntrada ? monto : esSalida ? -monto : 0,
    };
  });

  const totalEntrada = data.reduce((s, r) => s + r.entrada, 0);
  const totalSalida = data.reduce((s, r) => s + r.salida, 0);

  return {
    rows: data,
    kpis: [
      { label: 'Movimientos', value: fmtInt(data.length), icon: BarChart3, accent: 'bg-blue-50 text-blue-600' },
      { label: 'Entradas', value: `$ ${fmtMoney(totalEntrada)}`, icon: TrendingUp, accent: 'bg-emerald-50 text-emerald-600' },
      { label: 'Salidas', value: `$ ${fmtMoney(totalSalida)}`, icon: TrendingUp, accent: 'bg-rose-50 text-rose-600' },
      {
        label: 'Neto',
        value: `$ ${fmtMoney(totalEntrada - totalSalida)}`,
        icon: Wallet,
        accent: 'bg-violet-50 text-violet-600',
      },
    ],
    totals: {
      entrada: `$ ${fmtMoney(totalEntrada)}`,
      salida: `$ ${fmtMoney(totalSalida)}`,
      neto: `$ ${fmtMoney(totalEntrada - totalSalida)}`,
    },
  };
}

// ─── CLIENTES ─────────────────────────────────────────────────
async function fetchTopClientes({ orgId, fromIso, toIso, siteId }) {
  // Reusa la lógica de ventas por cliente, pero ordena/limita
  const result = await fetchVentasPorCliente({ orgId, fromIso, toIso, siteId });
  return {
    ...result,
    rows: result.rows.slice(0, 50),
  };
}

async function fetchClientesInactivos({ orgId, fromIso }) {
  // Clientes activos sin ventas desde 'fromIso'
  const clients = await fetchAllPaginated(() =>
    supabase
      .from('clients')
      .select('id, full_name, doc_number, email, phone, balance, current_balance, created_at')
      .eq('organization_id', orgId)
      .eq('is_active', true)
      .order('full_name', { ascending: true })
  );

  const ids = clients.map((c) => c.id);
  let activos = new Set();
  if (ids.length > 0) {
    // Paginar en chunks de IDs para evitar URLs gigantes y el límite de 1000 filas
    const CHUNK = 200;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const slice = ids.slice(i, i + CHUNK);
      const ventas = await fetchAllPaginated(() =>
        supabase
          .from('sales')
          .select('client_id')
          .eq('organization_id', orgId)
          .gte('created_at', fromIso)
          .not('status', 'in', SALE_STATUS_NOT_IN)
          .in('client_id', slice)
          .order('created_at', { ascending: false })
      );
      ventas.forEach((s) => {
        if (s.client_id) activos.add(s.client_id);
      });
    }
  }

  const data = clients
    .filter((c) => !activos.has(c.id))
    .map((c) => ({
      _key: c.id,
      cliente: c.full_name || 'Sin nombre',
      doc: c.doc_number || '-',
      email: c.email || '-',
      telefono: c.phone || '-',
      saldo: getClientBalanceValue(c),
    }))
    .sort((a, b) => b.saldo - a.saldo);

  return {
    rows: data,
    kpis: [
      { label: 'Clientes inactivos', value: fmtInt(data.length), icon: Users, accent: 'bg-rose-50 text-rose-600' },
      {
        label: 'Saldo total',
        value: `$ ${fmtMoney(data.reduce((s, r) => s + r.saldo, 0))}`,
        icon: Wallet,
        accent: 'bg-amber-50 text-amber-600',
      },
    ],
    totals: { saldo: `$ ${fmtMoney(data.reduce((s, r) => s + r.saldo, 0))}` },
  };
}

async function fetchClientesNuevos({ orgId, fromIso, toIso }) {
  const data = await fetchAllPaginated(() =>
    supabase
      .from('clients')
      .select('id, full_name, doc_number, email, phone, created_at, balance')
      .eq('organization_id', orgId)
      .gte('created_at', fromIso)
      .lte('created_at', toIso)
      .order('created_at', { ascending: false })
  );

  const rows = (data || []).map((c) => ({
    _key: c.id,
    fecha: c.created_at,
    cliente: c.full_name || 'Sin nombre',
    doc: c.doc_number || '-',
    email: c.email || '-',
    telefono: c.phone || '-',
  }));

  return {
    rows,
    kpis: [{ label: 'Clientes nuevos', value: fmtInt(rows.length), icon: Users, accent: 'bg-emerald-50 text-emerald-600' }],
  };
}

/* ──────────────────────────────────────────────────────────────
   Report registry
   ────────────────────────────────────────────────────────────── */
const MONEY_RIGHT = { align: 'right' };

const REPORTS = {
  ventas: {
    label: 'Ventas',
    icon: ShoppingBag,
    accent: 'text-blue-600',
    reports: [
      {
        id: 'productos-vendidos',
        label: 'Productos vendidos',
        description: 'Ranking de SKUs por ingreso, costo y margen.',
        fetch: fetchProductosVendidos,
        columns: [
          { key: 'producto', label: 'Producto', bold: true },
          { key: 'marca', label: 'Marca' },
          { key: 'codigo', label: 'Código' },
          { key: 'cantidad', label: 'Unidades', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'ingreso', label: 'Ingreso', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'costo', label: 'Costo', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'margen', label: 'Margen', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'margenPct', label: '% Margen', ...MONEY_RIGHT, render: (v) => fmtPct(v) },
        ],
      },
      {
        id: 'ventas-por-cliente',
        label: 'Ventas por cliente',
        description: 'Facturación, cantidad de tickets y ticket promedio por cliente.',
        fetch: fetchVentasPorCliente,
        columns: [
          { key: 'cliente', label: 'Cliente', bold: true },
          { key: 'doc', label: 'CUIT/DNI' },
          { key: 'tickets', label: 'Tickets', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'total', label: 'Total', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'ticketProm', label: 'Ticket prom.', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
      {
        id: 'ventas-por-metodo',
        label: 'Por método de cobro',
        description: 'Distribución de ventas por método de cobro.',
        fetch: fetchVentasPorMetodoCobro,
        columns: [
          { key: 'metodo', label: 'Método', bold: true },
          { key: 'tickets', label: 'Tickets', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'total', label: 'Total', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'pct', label: '% del total', ...MONEY_RIGHT, render: (v) => fmtPct(v) },
        ],
      },
      {
        id: 'ventas-por-sitio',
        label: 'Por sucursal',
        description: 'Comparativa de ventas entre sucursales.',
        fetch: fetchVentasPorSitio,
        ignoreSiteFilter: true,
        columns: [
          { key: 'sitio', label: 'Sucursal', bold: true },
          { key: 'tickets', label: 'Tickets', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'total', label: 'Total', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'ticketProm', label: 'Ticket prom.', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'pct', label: '% del total', ...MONEY_RIGHT, render: (v) => fmtPct(v) },
        ],
      },
      {
        id: 'ventas-por-vendedor',
        label: 'Por vendedor',
        description: 'Ranking de vendedores por facturación y tickets.',
        fetch: fetchVentasPorVendedor,
        columns: [
          { key: 'vendedor', label: 'Vendedor', bold: true },
          { key: 'email', label: 'Email' },
          { key: 'tickets', label: 'Tickets', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'total', label: 'Total', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'ticketProm', label: 'Ticket prom.', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
      {
        id: 'productos-no-vendidos',
        label: 'Productos sin venta',
        description: 'Catálogo activo que no se vendió en el período (oportunidad / stock muerto).',
        fetch: fetchProductosNoVendidos,
        columns: [
          { key: 'producto', label: 'Producto', bold: true },
          { key: 'marca', label: 'Marca' },
          { key: 'codigo', label: 'Código' },
          { key: 'stock', label: 'Stock', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'costo', label: 'Costo', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'precio', label: 'Precio venta', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'valorStock', label: 'Valor inmov.', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
    ],
  },
  inventario: {
    label: 'Inventario',
    icon: Boxes,
    accent: 'text-emerald-600',
    reports: [
      {
        id: 'stock-valorizado',
        label: 'Stock valorizado',
        description: 'Valor actual del inventario a costo y a precio de venta.',
        fetch: fetchStockValorizado,
        usesPeriod: false,
        columns: [
          { key: 'producto', label: 'Producto', bold: true },
          { key: 'marca', label: 'Marca' },
          { key: 'codigo', label: 'Código' },
          { key: 'stock', label: 'Stock', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'costoUnit', label: 'Costo unit.', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'valorCosto', label: 'Valor costo', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'precio', label: 'Precio venta', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'valorVenta', label: 'Valor venta', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'margenPotencial', label: 'Margen pot.', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
      {
        id: 'bajo-minimo',
        label: 'Bajo mínimo',
        description: 'Productos con stock por debajo del mínimo definido.',
        fetch: fetchBajoMinimo,
        usesPeriod: false,
        columns: [
          { key: 'producto', label: 'Producto', bold: true },
          { key: 'marca', label: 'Marca' },
          { key: 'codigo', label: 'Código' },
          { key: 'stock', label: 'Stock', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'minimo', label: 'Mínimo', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'faltante', label: 'Falta', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'costoUnit', label: 'Costo unit.', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'reposicion', label: 'Costo reposición', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
      {
        id: 'rotacion',
        label: 'Rotación',
        description: 'Días de cobertura por SKU (stock actual vs venta diaria promedio).',
        fetch: fetchRotacion,
        columns: [
          { key: 'producto', label: 'Producto', bold: true },
          { key: 'marca', label: 'Marca' },
          { key: 'stock', label: 'Stock', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'vendido', label: 'Vendido período', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'promDia', label: 'Venta prom. diaria', ...MONEY_RIGHT, render: (v) => Number(v).toLocaleString('es-AR', { maximumFractionDigits: 2 }) },
          {
            key: 'cobertura',
            label: 'Cobertura (días)',
            ...MONEY_RIGHT,
            render: (v) => (v == null || !Number.isFinite(v) ? '∞' : Math.round(v).toLocaleString('es-AR')),
          },
        ],
      },
      {
        id: 'kardex',
        label: 'Kardex / Movimientos',
        description: 'Historial de entradas, salidas y transferencias en el período.',
        fetch: fetchKardex,
        columns: [
          { key: 'fecha', label: 'Fecha', render: (v) => fmtDate(v) },
          { key: 'producto', label: 'Producto', bold: true },
          { key: 'marca', label: 'Marca' },
          { key: 'tipo', label: 'Tipo' },
          { key: 'ingreso', label: 'Ingreso', ...MONEY_RIGHT, render: (v) => (v ? fmtInt(v) : '-') },
          { key: 'egreso', label: 'Egreso', ...MONEY_RIGHT, render: (v) => (v ? fmtInt(v) : '-') },
          { key: 'motivo', label: 'Motivo' },
        ],
      },
    ],
  },
  compras: {
    label: 'Compras',
    icon: Package,
    accent: 'text-violet-600',
    reports: [
      {
        id: 'compras-por-proveedor',
        label: 'Por proveedor',
        description: 'Ranking de proveedores por monto y cantidad de operaciones.',
        fetch: fetchComprasPorProveedor,
        ignoreSiteFilter: true,
        columns: [
          { key: 'proveedor', label: 'Proveedor', bold: true },
          { key: 'ops', label: 'Operaciones', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'total', label: 'Total', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'opProm', label: 'Operación prom.', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
      {
        id: 'productos-mas-comprados',
        label: 'Productos más comprados',
        description: 'Items con mayor volumen e inversión de compra.',
        fetch: fetchProductosMasComprados,
        ignoreSiteFilter: true,
        columns: [
          { key: 'producto', label: 'Producto', bold: true },
          { key: 'cantidad', label: 'Unidades', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'costo', label: 'Total invertido', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'costoProm', label: 'Costo prom.', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
      {
        id: 'evolucion-costos',
        label: 'Evolución de costos',
        description: 'Cambios registrados en el costo de productos durante el período.',
        fetch: fetchEvolucionCostos,
        ignoreSiteFilter: true,
        columns: [
          { key: 'fecha', label: 'Fecha', render: (v) => fmtDate(v) },
          { key: 'producto', label: 'Producto', bold: true },
          { key: 'marca', label: 'Marca' },
          { key: 'origen', label: 'Origen' },
          { key: 'costoPrev', label: 'Costo previo', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'costoNuevo', label: 'Costo nuevo', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'variacion', label: 'Variación', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'variacionPct', label: '% Variación', ...MONEY_RIGHT, render: (v) => fmtPct(v) },
        ],
      },
    ],
  },
  tesoreria: {
    label: 'Tesorería',
    icon: Wallet,
    accent: 'text-amber-600',
    reports: [
      {
        id: 'gastos-por-categoria',
        label: 'Gastos por categoría',
        description: 'Desglose de gastos del período por categoría.',
        fetch: fetchGastosPorCategoria,
        columns: [
          { key: 'categoria', label: 'Categoría', bold: true },
          { key: 'cantidad', label: 'Cantidad', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'total', label: 'Total', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'pct', label: '% del total', ...MONEY_RIGHT, render: (v) => fmtPct(v) },
        ],
      },
      {
        id: 'cxc-aging',
        label: 'Cuentas por cobrar',
        description: 'Saldos de clientes ordenados por antigüedad.',
        fetch: fetchAgingCobrar,
        usesPeriod: false,
        ignoreSiteFilter: true,
        columns: [
          { key: 'cliente', label: 'Cliente', bold: true },
          { key: 'doc', label: 'CUIT/DNI' },
          { key: 'ultMov', label: 'Último mov.', render: (v) => fmtDate(v) },
          { key: 'dias', label: 'Días', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'bucket', label: 'Antigüedad' },
          { key: 'saldo', label: 'Saldo', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
      {
        id: 'cxp-aging',
        label: 'Cuentas por pagar',
        description: 'Saldos a proveedores ordenados por antigüedad.',
        fetch: fetchAgingPagar,
        usesPeriod: false,
        ignoreSiteFilter: true,
        columns: [
          { key: 'proveedor', label: 'Proveedor', bold: true },
          { key: 'cuit', label: 'CUIT' },
          { key: 'ultMov', label: 'Último mov.', render: (v) => fmtDate(v) },
          { key: 'dias', label: 'Días', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'bucket', label: 'Antigüedad' },
          { key: 'saldo', label: 'Saldo', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
      {
        id: 'cobros-y-pagos',
        label: 'Cobros y pagos',
        description: 'Movimientos financieros (entradas, salidas y neto) del período.',
        fetch: fetchCobrosYPagos,
        columns: [
          { key: 'fecha', label: 'Fecha', render: (v) => fmtDate(v) },
          { key: 'tipo', label: 'Tipo' },
          { key: 'categoria', label: 'Categoría' },
          { key: 'descripcion', label: 'Descripción' },
          { key: 'entrada', label: 'Entrada', ...MONEY_RIGHT, render: (v) => (v ? `$ ${fmtMoney(v)}` : '-') },
          { key: 'salida', label: 'Salida', ...MONEY_RIGHT, render: (v) => (v ? `$ ${fmtMoney(v)}` : '-') },
          { key: 'neto', label: 'Neto', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
    ],
  },
  clientes: {
    label: 'Clientes',
    icon: Users,
    accent: 'text-rose-600',
    reports: [
      {
        id: 'top-clientes',
        label: 'Top 50 clientes',
        description: 'Mejores 50 clientes por facturación del período.',
        fetch: fetchTopClientes,
        columns: [
          { key: 'cliente', label: 'Cliente', bold: true },
          { key: 'doc', label: 'CUIT/DNI' },
          { key: 'tickets', label: 'Tickets', ...MONEY_RIGHT, render: (v) => fmtInt(v) },
          { key: 'total', label: 'Total', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
          { key: 'ticketProm', label: 'Ticket prom.', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
      {
        id: 'clientes-inactivos',
        label: 'Clientes inactivos',
        description: 'Clientes activos sin ventas dentro del período (desde la fecha "desde").',
        fetch: fetchClientesInactivos,
        ignoreSiteFilter: true,
        columns: [
          { key: 'cliente', label: 'Cliente', bold: true },
          { key: 'doc', label: 'CUIT/DNI' },
          { key: 'email', label: 'Email' },
          { key: 'telefono', label: 'Teléfono' },
          { key: 'saldo', label: 'Saldo', ...MONEY_RIGHT, render: (v) => `$ ${fmtMoney(v)}` },
        ],
      },
      {
        id: 'clientes-nuevos',
        label: 'Clientes nuevos',
        description: 'Altas de clientes realizadas en el período.',
        fetch: fetchClientesNuevos,
        ignoreSiteFilter: true,
        columns: [
          { key: 'fecha', label: 'Fecha alta', render: (v) => fmtDate(v) },
          { key: 'cliente', label: 'Cliente', bold: true },
          { key: 'doc', label: 'CUIT/DNI' },
          { key: 'email', label: 'Email' },
          { key: 'telefono', label: 'Teléfono' },
        ],
      },
    ],
  },
};

const TAB_ORDER = ['ventas', 'inventario', 'compras', 'tesoreria', 'clientes'];

const PERIOD_PRESETS = [
  { id: 'today', label: 'Hoy', days: 1 },
  { id: 'week', label: '7 días', days: 7 },
  { id: 'month', label: '30 días', days: 30 },
  { id: 'quarter', label: '90 días', days: 90 },
  { id: 'year', label: '365 días', days: 365 },
];

/* ──────────────────────────────────────────────────────────────
   Main component
   ────────────────────────────────────────────────────────────── */
export default function Reportes() {
  const { profile } = useAuth();
  const { sites } = useSite();
  const orgId = profile?.organization_id;

  const [tab, setTab] = useState('ventas');
  const [reportId, setReportId] = useState(REPORTS.ventas.reports[0].id);

  const initial = useMemo(() => presetRange(30), []);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [siteId, setSiteId] = useState('');
  const [period, setPeriod] = useState('month');

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState({ rows: [], kpis: [], totals: null });

  const activeReport = useMemo(
    () => REPORTS[tab].reports.find((r) => r.id === reportId) || REPORTS[tab].reports[0],
    [tab, reportId]
  );

  const applyPreset = (id) => {
    const cfg = PERIOD_PRESETS.find((p) => p.id === id);
    if (!cfg) return;
    const r = presetRange(cfg.days);
    setDateFrom(r.from);
    setDateTo(r.to);
    setPeriod(id);
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setReportId(REPORTS[newTab].reports[0].id);
  };

  const load = useCallback(async () => {
    if (!orgId || !activeReport) return;
    setLoading(true);
    try {
      const fromIso = `${dateFrom}T00:00:00.000Z`;
      const toIso = `${dateTo}T23:59:59.999Z`;
      const ctx = {
        orgId,
        fromIso,
        toIso,
        fromDate: dateFrom,
        toDate: dateTo,
        siteId: activeReport.ignoreSiteFilter ? '' : siteId || '',
        sites: sites || [],
      };
      const res = await activeReport.fetch(ctx);
      setResult({ rows: res.rows || [], kpis: res.kpis || [], totals: res.totals || null });
    } catch (err) {
      console.error('[Reportes]', err);
      toast.error('Error al cargar el reporte');
      setResult({ rows: [], kpis: [], totals: null });
    } finally {
      setLoading(false);
    }
  }, [orgId, activeReport, dateFrom, dateTo, siteId, sites]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = () => {
    const fname = `${activeReport.id}_${dateFrom}_${dateTo}`;
    exportCsv(fname, activeReport.columns, result.rows);
  };

  return (
    <div className="w-full p-4 md:p-6 lg:p-8 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 uppercase tracking-tight leading-none">
            Reportes
          </h1>
          <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest mt-2 italic">
            Reportes detallados, filtrables y exportables por categoría
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl font-bold uppercase text-[10px] tracking-widest shadow-sm hover:border-[#51A7FD] hover:text-[#51A7FD] transition"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Recalcular
          </button>
          <button
            onClick={handleExport}
            disabled={!result.rows.length}
            className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl font-bold uppercase text-[10px] tracking-widest shadow-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <Download size={14} /> Exportar CSV
          </button>
        </div>
      </div>

      {/* Tabs principales */}
      <div className="flex flex-wrap gap-2 mb-4">
        {TAB_ORDER.map((id) => {
          const t = REPORTS[id];
          const Icon = t.icon;
          const active = id === tab;
          return (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest transition border ${
                active
                  ? 'bg-white border-slate-300 text-slate-900 shadow-sm'
                  : 'bg-slate-100/60 border-transparent text-slate-500 hover:text-slate-700 hover:bg-white'
              }`}
            >
              <Icon size={14} className={active ? t.accent : 'text-slate-400'} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Sub-tabs (reportes) */}
      <div className="flex flex-wrap gap-2 mb-5">
        {REPORTS[tab].reports.map((r) => {
          const active = r.id === reportId;
          return (
            <button
              key={r.id}
              onClick={() => setReportId(r.id)}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest transition border ${
                active
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
              }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-5 flex flex-col md:flex-row md:items-end gap-3">
        {activeReport.usesPeriod !== false && (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Desde</label>
              <input
                type="date"
                value={dateFrom}
                max={dateTo}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPeriod('custom');
                }}
                className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Hasta</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPeriod('custom');
                }}
                className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {PERIOD_PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => applyPreset(p.id)}
                  className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest border transition ${
                    period === p.id
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </>
        )}
        {!activeReport.ignoreSiteFilter && (
          <div className="flex flex-col gap-1">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Sucursal</label>
            <select
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs min-w-[160px]"
            >
              <option value="">Todas</option>
              {(sites || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="ml-auto text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden md:block">
          {activeReport.description}
        </div>
      </div>

      {/* KPIs */}
      {result.kpis?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {result.kpis.map((k, i) => (
            <KpiCard key={i} icon={k.icon} label={k.label} value={k.value} accent={k.accent} hint={k.hint} />
          ))}
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BarChart3 size={14} className="text-slate-400" />
            <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-700">
              {activeReport.label}
            </h2>
            <span className="text-[10px] font-bold text-slate-400">· {fmtInt(result.rows.length)} filas</span>
          </div>
          <span className="text-[10px] font-bold text-slate-400 md:hidden">{activeReport.description}</span>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="py-16 flex flex-col items-center gap-3 text-slate-400">
              <RefreshCw size={22} className="animate-spin" />
              <p className="text-[10px] font-black uppercase tracking-widest">Cargando...</p>
            </div>
          ) : (
            <ReportTable columns={activeReport.columns} rows={result.rows} totals={result.totals} />
          )}
        </div>
      </div>
    </div>
  );
}
